import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { HttpService } from 'src/app/http.service';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-player-form',
  templateUrl: './player-form.component.html',
  styleUrls: ['./player-form.component.scss']
})
export class PlayerFormComponent {

  @Input() fullwidth: boolean = false;
  @Input() set playerUUID(val:any) {
    this.init(val)
  };
  @Output() successEvent = new EventEmitter();
  @Output() closeEvent = new EventEmitter();

  public data : any = {status: 1, can_login: 0, is_agent: 0, banks: [{}]};
  public agentDepth :any = [];
  public permission:any = {};
  public new_permission:any = {};
  public player_levels: any[] = [];
  public level_log: any[] = [];
  public staging: any = { enabled: false, balance: '0.0000' };
  public stagingReady = false;
  public stagingSaving = false;
  public stagingMessage = '';
  // When the company runs Red Share Mode (红股差额), agent_rate is a WHOLE-number
  // percentage (50 = 50%) rather than the legacy decimal fraction (0.50 = 0.50%).
  // The rate picker below switches its option set based on this flag.
  public redShareMode = false;
  public agentRateOptions = this.buildAgentRateOptions();
  @ViewChild('f') f : NgForm

  constructor(private http: HttpService, private route: ActivatedRoute) {
    const uuid: Observable<string> = this.route.params.pipe(map(p => p['uuid']));

    uuid.subscribe(val => {
      if(val) {
        this.init(val)
      }
    })

    // Load the commission mode once (independent of create/edit) so the agent-rate
    // picker offers the correct units even when adding a brand-new agent.
    this.loadCommissionMode();
  }

  private async loadCommissionMode() {
    try {
      const s: any = await this.http.post('/admin/setting/get', {});
      const v = s ? s.agent_red_share_mode : null;
      this.redShareMode = (v === true || v === 'true' || v === 1 || v === '1');
    } catch (_) {
      this.redShareMode = false;
    }
    this.agentRateOptions = this.buildAgentRateOptions();
  }

  async init(uuid) {
    this.new_permission = await this.http.getHiddenPermission();

    this.data = await this.http.post('/admin/player/get', {uuid});
    this.stagingReady = false;
    this.stagingMessage = '';
    if (this.data.uuid) {
      const stagingUUID = this.data.uuid;
      try {
        const staging: any = await this.http.post('/admin/player/staging/get', {uuid: stagingUUID});
        if (this.data.uuid === stagingUUID && staging && staging.status) {
          this.staging = staging;
          this.stagingReady = true;
        }
      } catch (_) { /* A missing staging migration does not block normal editing. */ }
    }

    if(!this.data.banks || this.data.banks.length == 0) {
      this.data.banks = [{}]
    }

    const val = this.http.getSelecetdCompanyDetail();
    this.permission = val;

    let company = this.http.getSelecetdCompanyDetail();

    if(!this.data.referral_rate) {
      let company = this.http.getSelecetdCompanyDetail();
      this.data.referral_rate = company.referral_rate ?? 0
    }

    if(!this.data.referral_rate) {
      let company = this.http.getSelecetdCompanyDetail();
      this.data.point_rate = company.referral_rate ?? 0
    }

    if(!this.data.referral_rate) {
      let company = this.http.getSelecetdCompanyDetail();
      this.data.rebate_rate = company.referral_rate ?? 0
    }


    this.agentDepth = await this.http.post('/admin/agent/package');

    const levels: any = await this.http.post('/admin/setting/player-level/get', {});
    this.player_levels = Array.isArray(levels) ? levels : (levels?.data ?? []);

    if (uuid && this.data.uuid) {
      const log = await this.http.post('/admin/player/level-log/get', { player_uuid: uuid });
      this.level_log = Array.isArray(log) ? log : [];
    }
  }

  async submit() {
    const status : any = await this.http.post("/admin/player/update", this.data);

    if (!this.fullwidth && this.data.uuid) {
      this.http.navigate(['/lo',this.http.getStaticSelectedCompany(),'player'])
      return;
    }

    if (status && status.status) {
      this.successEvent.emit(status);
      this.f.resetForm({status: 1, can_login: 0});
    }
  }

  async saveStaging() {
    if (!this.stagingReady || this.stagingSaving || !this.data.uuid) return;
    this.stagingSaving = true;
    this.stagingMessage = '';
    try {
      const result: any = await this.http.post('/admin/player/staging/set', {
        uuid: this.data.uuid, enabled: this.staging.enabled === true
      });
      this.stagingMessage = result && result.status ? 'Staging permission saved.' : 'Could not save staging permission.';
    } catch (_) {
      this.stagingMessage = 'Could not save staging permission.';
    } finally {
      this.stagingSaving = false;
    }
  }


  async addBank() {
    if (this.data.banks.length >= 10) {
      alert("You can add upto 10 bank.");
      return;
    }
    this.data.banks.push({})
  }

  async removeBank(index) {
    if (this.data.banks.length <= 1) {
      alert("Minimum 1 bank required.");
      return;
    }
    this.data.banks.splice(index, 1);
  }

  close() {
    this.closeEvent.emit(true)
  }

  private buildAgentRateOptions() {
    const options = [];

    if (this.redShareMode) {
      // Red Share Mode: whole-number percentages (e.g. 50 = 50%). Stored verbatim;
      // each upline automatically earns the rate spread over its downline subtree.
      for (let i = 100; i >= 1; i--) {
        options.push({ value: i, label: `${i}%` });
      }
      options.push({ value: 0, label: '0%' });
      return options;
    }

    // Legacy decimal model (unchanged): 0.70% down to 0.001%.
    for (let i = 70; i >= 1; i--) {
      const value = i / 100;
      options.push({ value, label: `${value.toFixed(2)}%` });
    }

    for (let i = 9; i >= 1; i--) {
      const value = i / 1000;
      options.push({ value, label: `${value.toFixed(3)}%` });
    }

    options.push({ value: 0, label: '0%' });
    return options;
  }
}
